import { useRef } from 'react';
import { ActionContextProvider } from './context/ActionContext';
import { LocaleProvider } from './context/LocaleContext';
import { NamespacesProvider } from './context/NamespacesContext';
import { ThemeProvider } from './theme/ThemeContext';
import { CARD_ROOT_CLASS, FocusRingStyles, useFocusRingVars } from './theme/FocusRing';
import { useCardStateMachine } from './hooks/useCardStateMachine';
import { RenderElement } from './renderer/renderElement';
import { sanitizeCardBody } from './sanitizer/sanitize';
import { EMPTY_TEXT } from './binding/defaults';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CardRenderer = ({
  body,
  onAction,
  locale,
  context,
  dynamicText,
  errorReason,
  renderKey,
  responseKey,
  idleTimeout,
  isReadOnly
}) => {
  const bodyVersionRef = useRef(0);
  const prevRenderKeyRef = useRef(renderKey);
  // eslint-disable-next-line react-hooks/refs
  if (renderKey !== prevRenderKeyRef.current) {
    // eslint-disable-next-line react-hooks/refs
    prevRenderKeyRef.current = renderKey;
    // eslint-disable-next-line react-hooks/refs
    bodyVersionRef.current++;
  }
  const {
    namespaces,
    mutableContext,
    onMutableContextChange,
    handleButtonClick,
    getActionState,
    isCardDisabled,
    allRequiredBindingsFilled
  } = useCardStateMachine({
    body,
    renderKey,
    responseKey,
    context,
    dynamicText,
    errorReason,
    idleTimeout,
    locale,
    onAction
  });
  const focusRingVars = useFocusRingVars();
  return /*#__PURE__*/_jsx(LocaleProvider, {
    locale: locale,
    children: /*#__PURE__*/_jsx(ActionContextProvider, {
      value: {
        onButtonClick: handleButtonClick,
        getActionState,
        isCardDisabled,
        allRequiredBindingsFilled,
        isReadOnly
      },
      children: /*#__PURE__*/_jsx(NamespacesProvider, {
        value: namespaces,
        children: /*#__PURE__*/_jsxs("div", {
          className: CARD_ROOT_CLASS,
          style: Object.assign({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }, focusRingVars),
          children: [/*#__PURE__*/_jsx(FocusRingStyles, {}), body.map((element, index) => /*#__PURE__*/_jsx(RenderElement, {
            element: element,
            mutableContext: mutableContext,
            onMutableContextChange: onMutableContextChange
          }, index))]
        }, bodyVersionRef.current)
      })
    })
  });
};
export function renderCard(body, onAction, options) {
  const {
    locale,
    keys,
    context = {},
    dynamicText = EMPTY_TEXT,
    errorReason = '',
    config,
    isReadOnly = false
  } = options;
  const {
    renderKey,
    responseKey
  } = keys;
  const sanitizedBody = sanitizeCardBody(body);
  if (sanitizedBody === null) {
    return null;
  }
  return /*#__PURE__*/_jsx(ThemeProvider, {
    children: /*#__PURE__*/_jsx(CardRenderer, {
      body: sanitizedBody,
      onAction: onAction,
      locale: locale,
      context: context,
      dynamicText: dynamicText,
      errorReason: errorReason,
      renderKey: renderKey,
      responseKey: responseKey,
      idleTimeout: config === null || config === void 0 ? void 0 : config.idleTimeout,
      isReadOnly: isReadOnly
    })
  });
}